import ReactFlow, {
  applyNodeChanges,
  ControlButton,
  Controls,
  EdgeChange,
  EdgeProps,
  MarkerType,
  Node,
  NodeChange,
  NodeProps,
} from "reactflow";
import { useAppDispatch, useAppSelector } from "store/hooks";
import "reactflow/dist/style.css";
import {
  changeNodeData,
  ConditionalSegmentsSettings,
  ConnectionStatus,
  deselectNodes,
  handleDevModeState,
  JourneyType,
  QueryStatement,
  recountAvailableNodes,
  resetDevMode,
  SegmentQueryStatement,
  setJumpToTargettingNode,
  setNodes,
} from "reducers/flow-builder.reducer";
import {
  EmptyNode,
  ExitNode,
  InsertNode,
  JumpToNode,
  MessageNode,
  StartNode,
  TimeDelayNode,
  TimeWindowNode,
  TrackerNode,
  UserAttributeNode,
  WaitUntilNode,
} from "./Nodes";
import FlowBuilderSidePanel from "./SidePanel/FlowBuilderSidePanel";
import { BranchEdge, PrimaryEdge } from "./Edges";
import { FC, useEffect, useRef, useState } from "react";
import NodeDraggingProvider from "./FlowPlugins/NodeDraggingProvider";
import Button, { ButtonType } from "components/Elements/Buttonv2";
import { JumpToNodeData } from "./Nodes/NodeData";
import { DevModeControlHint } from "./DevModeControlHint";
import useDevKeysHandler from "./useDevKeysHandler";
import { useDevSocket } from "./useDevSocketConnection";
import { MultisplitNode } from "./Nodes/MultisplitNode";
import { ExperimentNode } from "./Nodes/ExperimentNode";
import Select from "components/Elements/Selectv2";
import Template, { TemplateType } from "types/Template";
import { MessageType, VisualLayout } from "types/Workflow";
import ApiService from "services/api.service";
import { ApiConfig } from "../../constants";
import { Segment } from "types/Segment";
import { convertToJounreyVisualLayout, toJourneyPayloadTemplate } from "./placeholders";
import { useParams } from "react-router-dom";
import { update } from "lodash";
import { useJourneyControls, useSimpleJourneyOptions } from "./hooks";
import { SegmentSizeBox } from "./Elements/SegmentSizeBox";

export enum NodeType {
  START = "start",
  EMPTY = "empty",
  MESSAGE = "message",
  JUMP_TO = "jumpTo",
  EXIT = "exit",
  WAIT_UNTIL = "waitUntil",
  TIME_DELAY = "timeDelay",
  PUSH = "push",
  TIME_WINDOW = "timeWindow",
  USER_ATTRIBUTE = "userAttribute",
  INSERT_NODE = "insertNode",
  TRACKER = "tracker",
  MULTISPLIT = "multisplit",
  EXPERIMENT = "experiment",
}

export enum EdgeType {
  PRIMARY = "primary",
  BRANCH = "branch",
}

const nodeTypes: Record<NodeType, FC<NodeProps<any>>> = {
  [NodeType.START]: StartNode,
  [NodeType.EMPTY]: EmptyNode,
  [NodeType.MESSAGE]: MessageNode,
  [NodeType.JUMP_TO]: JumpToNode,
  [NodeType.EXIT]: ExitNode,
  [NodeType.WAIT_UNTIL]: WaitUntilNode,
  [NodeType.TIME_DELAY]: TimeDelayNode,
  [NodeType.TIME_WINDOW]: TimeWindowNode,
  [NodeType.USER_ATTRIBUTE]: UserAttributeNode,
  [NodeType.INSERT_NODE]: InsertNode,
  [NodeType.TRACKER]: TrackerNode,
  [NodeType.MULTISPLIT]: MultisplitNode,
  [NodeType.PUSH]: MessageNode,
  [NodeType.EXPERIMENT]: ExperimentNode,
};

const edgeTypes: Record<EdgeType, FC<EdgeProps<any>>> = {
  [EdgeType.PRIMARY]: PrimaryEdge,
  [EdgeType.BRANCH]: BranchEdge,
};

interface FlowFormEditorProps {
  isViewMode?: boolean;
  className?: string;
  setSimpleJourney?: (data: any) => void;
  simpleJourney?: any;
  updateJourney: (data: any) => void;
  isActive?: boolean;
}

export const nodeTypesNotConnectableByJumpTo: (string | undefined)[] = [
  NodeType.EMPTY,
  NodeType.INSERT_NODE,
  NodeType.JUMP_TO,
  NodeType.START,
];

const FlowFormEditor: FC<FlowFormEditorProps> = ({
  className,
  setSimpleJourney,
  simpleJourney,
  isActive
}) => {
  const { channelsOptions, templatesOptions, audienceOptions, onChannelChange } = useSimpleJourneyOptions();


  const onFormFieldsChange = (key: string, value: any) => {
    setSimpleJourney?.((prev: any) => ({ ...prev, [key]: value }));
  }

  useEffect(() => {
    onChannelChange(simpleJourney.channel);
  }, [simpleJourney.channel]);



  const onChannelTemplateUpdate = (id: string) => {
    const template = templatesOptions.find((item) => item.key === Number(id));
    setSimpleJourney?.((prev: any) => ({ ...prev, template: { id: template?.key, name: template?.title } } as any));
  }
  const hasTemplates = templatesOptions.length > 0;
  return (
    <div
      className={`relative w-full p-4 h-full bg-[#F3F4F6] text-[#111827] flex flex-col ${className ? className : ""
        }`}
    >
      <div className='bg-white rounded flex flex-1 justify-center items-center p-5 select-none'>
        <div className=" p-2 flex-wrap gap-[30px] items-center flex flex-col">
          <div className="bg-white rounded-[8px] p-2 px-5 border border-gray-300"> Start </div>
          <div className="bg-white rounded-[8px] flex flex-col p-4 gap-3 border border-gray-300 w-[400px]">
            <div className="flex gap-2 items-center">
              <span>
                Target Channel:
              </span>
              <Select
                disabled={isActive}
                className="flex-1"
                options={channelsOptions}
                placeholder="Select channel"
                onChange={(val) => onFormFieldsChange('channel', val!)}
                value={simpleJourney.channel}
              />
            </div>
          </div>
          {hasTemplates ? <div className="bg-white rounded-[8px] flex flex-col p-4 gap-3 border border-gray-300 w-[400px]">
            <span className="capitalize">
              {`Select ${simpleJourney.channel} Template:`}
            </span>
            <Select
              disabled={isActive}
              className="rounded-[8px] flex-1"
              options={templatesOptions as any}
              placeholder="Select channel"
              onChange={onChannelTemplateUpdate}
              value={simpleJourney.template?.id as any}
            />
          </div> : null}
          <div className="bg-white rounded-[8px] flex flex-col p-4 gap-3 border border-gray-300 w-[400px]">
            <div className="flex gap-2 items-center">
              <span>
                Select your Audience:
              </span>
              <Select
                disabled={isActive}
                className="flex-1"
                options={audienceOptions}
                placeholder="Select Segment"
                onChange={(val) => onFormFieldsChange('audienceSegment', val!)}
                value={simpleJourney.audienceSegment}
              />
            </div>
            {(simpleJourney.audienceSegment && !isActive) && <SegmentSizeBox segmentId={simpleJourney.audienceSegment} />}
          </div>
          <div className="bg-white rounded-[8px] p-2 px-5 border border-gray-300"> Exit </div>
        </div>
      </div>
    </div >
  );
};

export default FlowFormEditor;
